<script setup>
import { useCaptchaManager } from '@0x234/common-vue/composables/captcha-manager';
import { useCountdownManager } from '@0x234/common-vue/composables/countdown-manager';
import { useNotifyManager } from '@0x234/common-vue/composables/notify-manager';
import { OtpError } from '@0x234/common-vue/errors/otp-error';
import AppLogo from 'components/AppLogo.vue';
import AppTerms from 'components/AppTerms.vue';
import CaptchaChallengeInput from 'components/form-input/CaptchaChallengeInput.vue';
import MarketNoteMark from 'components/market/MarketNoteMark.vue';
import SocialLogin from 'components/SocialLogin.vue';
import { usePageMeta } from 'composables/page-meta';
import { useQuasar } from 'quasar';
import { useOtpManager } from 'src/composables/otp-manager';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { formatMarketMetric } from 'src/utils/market-util';
import { buildPageMeta } from 'src/utils/page-meta-util';
import { useAuthStore } from 'stores/auth-store';
import { useMarketStore } from 'stores/market-store';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

defineOptions({
    async preFetch() {
        // The evidence is what the page argues with, but it must never keep anyone from logging in, so loadSummary
        // answers null rather than throwing and the column reads its own empty state.
        await useMarketStore().loadSummary();
    }
});

const { message: errorMessage, isCountdownActive, initCountdown, resetCountdown } = useCountdownManager();
const { challengeInputRef, challengeModel, challengeRequest, isChallengeRequired, handleCaptchaChallenge, resetChallengeState } = useCaptchaManager();

const $q = useQuasar();
const $route = useRoute();
const $router = useRouter();
const authStore = useAuthStore();
const marketStore = useMarketStore();
const notifyManager = useNotifyManager();
const otpManager = useOtpManager();

// One line of evidence, withheld whole when the figures do not support it rather than shown in parts.
const marketLine = computed(() => {
    const salary = marketStore.summary?.salaries;
    const jobCount = marketStore.summary?.jobCount;
    if (!salary?.isSampleSufficient || !Number.isFinite(salary.median) || !Number.isFinite(jobCount)) {
        return null;
    }
    const median = formatMarketMetric(salary.median, { type: 'salary', currency: salary.currency || 'AUD' });
    return `Median ${median} across ${formatMarketMetric(jobCount)} active Australian ICT roles`;
});

const formElement = ref();
const formModel = ref({
    email: null
});

if ($route.query.status === '0' && $route.query.type === 'login') {
    errorMessage.value = 'Login failed. Your email may already be associated with another login method. Please try logging in with your email instead.';
}

async function onFormSubmit() {
    resetCountdown();
    $q.loading.show();
    await handleCaptchaChallenge(
        async captcha => {
            const token = await authStore.requestLoginOtp(formModel.value.email, captcha);
            otpManager.openOtpDialog(token, formModel.value.email).onOk(() => {
                $router.push(AppRoute.build(Route.Dashboard));
            });
            resetChallengeState();
        },
        err => {
            if (err instanceof OtpError && err.details.retryAfter) {
                initCountdown(err.details.retryAfter, 'Too many attempts, please try again in');
                return;
            }
            if (err?.response?.status === 403) {
                $router.push(AppRoute.build(Route.Dashboard));
                notifyManager.notifyWarning('You are already logged in!');
                return;
            }
            errorMessage.value = 'Something went wrong, please restart the login process.';
        },
        () => {
            $q.loading.hide();
        }
    );
}

usePageMeta(buildPageMeta({
    title: 'Login',
    description: 'Log in to Xprt Dev to save an Australian tech market, follow its latest figures and manage your '
        + 'optional career profile.',
    robots: 'noindex,follow'
}));
</script>

<template>
    <q-page id="login-page" class="login-page market-hero flex column justify-center q-py-xl">
        <div class="login-container q-px-md text-center">
            <AppLogo class="q-mx-auto q-mb-xl" height="34px"/>
            <h1 class="login-headline text-h4 text-sm-h3 text-weight-bold q-mx-auto q-mt-none q-mb-md">
                Know what the market pays before you ask.
            </h1>
            <p v-if="marketLine" class="font-mono text-caption text-hint q-mx-auto q-mt-none q-mb-xl">
                {{ marketLine }}<MarketNoteMark :number="2"/>
            </p>
            <div class="login-panel readout-frame q-mx-auto q-pa-xl">
                <p class="font-mono text-caption text-uppercase text-hint q-mt-none q-mb-md">Xprt Dev · Login</p>
                <h2 class="text-h4 text-weight-bold q-mt-none q-mb-sm">Log in to Xprt Dev</h2>
                <p class="text-body2 text-hint q-mt-none q-mb-lg">
                    Enter your email, or continue with a linked service.
                </p>
                <div class="email-form q-mx-auto">
                    <div v-if="errorMessage" class="login-error text-negative text-weight-medium q-mb-lg">{{ errorMessage }}</div>
                    <q-form ref="formElement" greedy @submit.prevent="onFormSubmit">
                        <div class="q-mb-md">
                            <q-input
                                v-model="formModel.email"
                                :readonly="isChallengeRequired"
                                :rules="[ v => !!v || 'Please enter your email' ]"
                                bottom-slots
                                label="Email"
                                lazy-rules
                                outlined
                                type="email"
                            />
                        </div>
                        <div v-if="isChallengeRequired" class="q-mb-md">
                            <CaptchaChallengeInput ref="challengeInputRef" v-model="challengeModel" :captcha-request="challengeRequest"/>
                        </div>
                        <q-btn :disable="isCountdownActive" color="primary" label="Log in with email" no-caps type="submit" unelevated/>
                    </q-form>
                </div>
                <div class="row items-center font-mono text-caption text-uppercase text-hint q-my-lg">
                    <q-separator class="col"/>
                    <span class="q-mx-sm">Or log in with</span>
                    <q-separator class="col"/>
                </div>
                <SocialLogin :hide-strategies="['email']"/>
            </div>
            <p class="login-account text-body2 text-hint q-mx-auto q-mt-xl q-mb-none">
                An account saves one market to come back to, and keeps a career profile that stays private until you
                share it.
            </p>
            <AppTerms class="q-mt-lg" inverted/>
        </div>
    </q-page>
</template>

<style lang="scss" scoped>
.login-page {
    border-bottom: 0;

    .login-container {
        width: 100%;
    }

    .login-headline {
        max-width: 18ch;
        text-wrap: balance;
    }

    .login-panel {
        max-width: 560px;
    }

    .login-account {
        max-width: 480px;
    }

    .email-form {
        max-width: 350px;
    }
}
</style>
